/**
 * OverrideReasonModal — Pflicht-Grund-Modal für Admin-Override-Aktionen.
 *
 * Eingesetzt für Vertragsänderungen bei ausgetretenen Mitarbeitern (#966 Bundle C).
 * Zeigt Headline + Hinweis-Text + Pflicht-Textarea (min 10 Zeichen) + 2 Buttons.
 *
 * KEIN native confirm/alert — eigenständiges Modal-Pattern, weil ConfirmDialog
 * keinen Textarea-Support hat.
 *
 * @component
 * @props {boolean} open
 * @props {() => void} onCancel
 * @props {(grund: string) => void} onConfirm — wird mit getrimmtem Grund aufgerufen
 * @props {string} title
 * @props {string} description — wird oberhalb der Textarea angezeigt (i18n-Text mit ggf. {{datum}})
 * @props {string} [confirmLabel='Bestätigen und bearbeiten']
 * @props {string} [cancelLabel='Abbrechen']
 * @props {number} [minLength=10]
 * @props {string} [placeholder]
 *
 * @since v2.418.0 (#966 Sprint 2 / Bundle C)
 */
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

export default function OverrideReasonModal({
  open,
  onCancel,
  onConfirm,
  title,
  description,
  confirmLabel = 'Bestätigen und bearbeiten',
  cancelLabel = 'Abbrechen',
  minLength = 10,
  placeholder,
  zIndex = 60,
}) {
  const [grund, setGrund] = useState('');
  const taRef = useRef(null);

  useEffect(() => {
    if (open) {
      setGrund('');
      setTimeout(() => taRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onCancel]);

  if (!open) return null;

  const trimmed = grund.trim();
  const tooShort = trimmed.length < minLength;
  const tooLong = trimmed.length > 1000;
  const disabled = tooShort || tooLong;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', zIndex }}
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-[520px] p-6"
        style={{ boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-50 shrink-0">
            <AlertTriangle size={18} className="text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
              {title}
            </h3>
            {description && (
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                {description}
              </p>
            )}
          </div>
        </div>

        <textarea
          ref={taRef}
          value={grund}
          onChange={e => setGrund(e.target.value)}
          placeholder={placeholder}
          rows={4}
          className="w-full text-sm px-3 py-2 rounded-lg border resize-none focus:outline-none focus:ring-2 focus:ring-[#338edc]"
          style={{
            color: 'var(--text-primary)',
            borderColor: 'var(--border-primary, #E5E7EB)',
            backgroundColor: 'var(--bg-card, #FFFFFF)',
          }}
        />

        <div className="flex items-center justify-between mt-2 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
          <span>
            {tooShort
              ? `Mindestens ${minLength} Zeichen — aktuell ${trimmed.length}`
              : tooLong
                ? `Maximal 1000 Zeichen — aktuell ${trimmed.length}`
                : `${trimmed.length} Zeichen`}
          </span>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium border"
            style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-primary, #E5E7EB)' }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={() => !disabled && onConfirm?.(trimmed)}
            disabled={disabled}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#338edc' }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
